import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Sun, 
  Leaf, 
  TrendingUp, 
  Shield,
  Clock,
  FileCheck,
  Zap,
  ArrowRight,
  CheckCircle2,
  Calculator,
  Home,
  Building2,
  Euro,
  Award
} from 'lucide-react';

const Inicio = () => {
  const services = [
    {
      icon: Sun,
      title: 'Instalación de Paneles Solares',
      description: 'Sistemas fotovoltaicos de alta eficiencia adaptados a tus necesidades energéticas.',
      link: '/servicios'
    },
    {
      icon: Leaf,
      title: 'Asesoramiento Energético',
      description: 'Análisis personalizado para maximizar tu ahorro y reducir tu huella de carbono.',
      link: '/servicios'
    },
    {
      icon: TrendingUp,
      title: 'Optimización del Consumo',
      description: 'Soluciones inteligentes para gestionar y optimizar tu consumo eléctrico.',
      link: '/servicios'
    }
  ];

  const benefits = [
    {
      icon: Euro,
      title: 'Ahorro Económico',
      description: 'Reduce tu factura eléctrica hasta un 70%',
      color: 'text-nubia-gold-primary'
    },
    {
      icon: Leaf,
      title: 'Energía Limpia',
      description: '100% renovable y sin emisiones',
      color: 'text-nubia-green-support'
    },
    {
      icon: Shield,
      title: 'Independencia',
      description: 'Protección contra subidas de precios',
      color: 'text-nubia-gold-primary'
    },
    {
      icon: Award,
      title: 'Valor Añadido',
      description: 'Aumenta el valor de tu propiedad',
      color: 'text-nubia-green-support'
    }
  ];

  const process = [
    {
      number: '01',
      title: 'Estudio Inicial',
      description: 'Analizamos tu consumo y espacio disponible para diseñar la solución perfecta.'
    },
    {
      number: '02',
      title: 'Presupuesto Detallado',
      description: 'Recibe una propuesta transparente con todas las opciones y estimaciones.'
    },
    {
      number: '03',
      title: 'Instalación Profesional',
      description: 'Nuestros técnicos certificados realizan la instalación en pocos días.'
    },
    {
      number: '04',
      title: 'Activación y Soporte',
      description: 'Gestionamos trámites y te acompañamos en cada paso del proceso.'
    }
  ];

  const faqs = [
    {
      question: '¿Cuánto puedo ahorrar con paneles solares?',
      answer: 'El ahorro promedio es del 70% en tu factura eléctrica, amortizando la inversión en 1-3 años.'
    },
    {
      question: '¿Cuánto tiempo dura la instalación?',
      answer: 'La instalación física se completa en 1-2 días. El proceso completo, incluyendo trámites, suele tardar 2 semanas.'
    },
    {
      question: '¿Hay subvenciones disponibles?',
      answer: 'Sí, te ayudamos a tramitar todas las bonificaciones y subvenciones que pueden cubrir hasta el 40% del coste.'
    }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Placas Solares Madrid, Valencia y Extremadura - Instalación Profesional | Nubia Solar</title>
        <meta name="description" content="Instalación de placas solares en Madrid, Valencia y Extremadura. Ahorra hasta 70% en tu factura eléctrica. Subvenciones, financiación y garantía. ☎️ 604 92 17 45" />
        <meta name="keywords" content="placas solares madrid, placas solares valencia, placas solares extremadura, instalación paneles solares, energía solar españa" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden bg-nubia-white">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1613665813446-82a78c468a1d" 
            alt="Instalación solar" 
            className="w-full h-full object-cover opacity-5"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-nubia-green-primary/5 to-nubia-gold-primary/5" />
        </div>
        
        <div className="container mx-auto px-4 z-10 text-center">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-nubia-gold-soft/20 text-nubia-green-primary px-5 py-2 rounded-full mb-8 font-medium border border-nubia-gold-primary/30">
              <Sun className="w-5 h-5" />
              Energía Solar en España
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold text-nubia-green-primary mb-6 leading-tight">
              Tu Futuro Energético<br />Comienza Aquí
            </h1>
            
            <p className="text-xl md:text-2xl text-nubia-text-dark/80 mb-10 leading-relaxed">
              Transforma tu hogar o negocio con energía solar limpia, eficiente y rentable. 
              Ahorra desde el primer día con Nubia Solar.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link to="/contacto">
                <Button size="lg" className="bg-nubia-gold-primary hover:bg-nubia-gold-primary/90 text-nubia-green-primary font-semibold px-10 py-7 text-lg shadow-xl hover:shadow-2xl transition-all">
                  Solicitar Presupuesto Gratis
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/servicios">
                <Button size="lg" variant="outline" className="border-2 border-nubia-green-primary text-nubia-green-primary hover:bg-nubia-green-primary hover:text-white px-10 py-7 text-lg transition-all">
                  Ver Servicios
                </Button>
              </Link>
            </div>
            
            <div className="flex flex-wrap justify-center gap-8 text-sm md:text-base">
              <div className="flex items-center gap-2 text-nubia-text-dark">
                <CheckCircle2 className="w-5 h-5 text-nubia-green-support" />
                <span>Más de 1000 instalaciones</span>
              </div>
              <div className="flex items-center gap-2 text-nubia-text-dark">
                <CheckCircle2 className="w-5 h-5 text-nubia-green-support" />
                <span>25 años de garantía</span>
              </div>
              <div className="flex items-center gap-2 text-nubia-text-dark">
                <CheckCircle2 className="w-5 h-5 text-nubia-green-support" />
                <span>Financiación disponible</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 bg-nubia-green-primary text-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <Clock className="w-12 h-12 text-nubia-gold-primary mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">Instalación en 2 Semanas</h3>
              <p className="text-nubia-white/80">Proceso rápido y eficiente</p>
            </div>
            <div>
              <FileCheck className="w-12 h-12 text-nubia-gold-primary mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">Trámites Incluidos</h3>
              <p className="text-nubia-white/80">Nos encargamos de todo</p>
            </div>
            <div>
              <Shield className="w-12 h-12 text-nubia-gold-primary mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">Ahorro Garantizado</h3>
              <p className="text-nubia-white/80">O te devolvemos tu dinero</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-nubia-green-primary mb-4">
              Nuestros Servicios
            </h2>
            <p className="text-xl text-nubia-text-dark/70 max-w-3xl mx-auto">
              Soluciones integrales de energía solar adaptadas a tus necesidades
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="nubia-shadow hover:nubia-shadow-lg transition-all border-nubia-gray-light group">
                  <CardHeader>
                    <div className="w-16 h-16 bg-nubia-gold-soft/20 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-nubia-gold-primary/20 transition-colors">
                      <Icon className="w-8 h-8 text-nubia-gold-primary" />
                    </div>
                    <CardTitle className="text-2xl text-nubia-green-primary">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-nubia-text-dark/70 mb-6 leading-relaxed">{service.description}</p>
                    <Link to={service.link}>
                      <Button variant="link" className="text-nubia-green-support hover:text-nubia-green-primary p-0 font-semibold">
                        Más información <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-nubia-gray-light">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-nubia-green-primary mb-4">
              Beneficios de la Energía Solar
            </h2>
            <p className="text-xl text-nubia-text-dark/70 max-w-3xl mx-auto">
              Descubre por qué miles de familias y empresas ya confían en nosotros
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 nubia-shadow">
                    <Icon className={`w-10 h-10 ${benefit.color}`} />
                  </div>
                  <h3 className="text-xl font-bold text-nubia-green-primary mb-3">{benefit.title}</h3>
                  <p className="text-nubia-text-dark/70">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-nubia-green-primary mb-4">
              Nuestro Proceso de Trabajo
            </h2>
            <p className="text-xl text-nubia-text-dark/70 max-w-3xl mx-auto">
              Cuatro pasos sencillos hacia tu independencia energética
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {process.map((step, index) => (
                <div key={index} className="relative">
                  <div className="text-8xl font-bold text-nubia-gold-soft/20 mb-4">{step.number}</div>
                  <h3 className="text-2xl font-bold text-nubia-green-primary mb-3">{step.title}</h3>
                  <p className="text-nubia-text-dark/70 leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Calculator CTA */}
      <section className="py-20 bg-nubia-green-secondary text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Calculator className="w-16 h-16 text-nubia-gold-primary mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Calcula Tu Ahorro Real
            </h2>
            <p className="text-xl text-nubia-white/90 mb-8 leading-relaxed">
              Descubre cuánto puedes ahorrar con nuestra calculadora solar personalizada. 
              Obtén una estimación precisa en menos de 2 minutos.
            </p>
            <Link to="/calculadora-solar">
              <Button size="lg" className="bg-nubia-gold-primary hover:bg-nubia-gold-primary/90 text-nubia-green-primary font-semibold px-10 py-7 text-lg shadow-xl">
                Calcular Mi Ahorro
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Mini FAQ */}
      <section className="py-24 bg-nubia-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-nubia-green-primary mb-4">
              Preguntas Frecuentes
            </h2>
            <p className="text-xl text-nubia-text-dark/70">
              Respuestas a las dudas más comunes
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto space-y-6">
            {faqs.map((faq, index) => (
              <Card key={index} className="border-2 border-nubia-gray-light hover:border-nubia-gold-primary/30 transition-colors">
                <CardHeader>
                  <CardTitle className="text-xl text-nubia-green-primary">{faq.question}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-nubia-text-dark/70 leading-relaxed">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/faq">
              <Button variant="outline" className="border-2 border-nubia-green-primary text-nubia-green-primary hover:bg-nubia-green-primary hover:text-white">
                Ver Todas las Preguntas
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonios Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-nubia-green-primary mb-4">
              Lo Que Dicen Nuestros Clientes
            </h2>
            <p className="text-xl text-nubia-text-dark/70">
              Más de 1,000 familias satisfechas en Madrid, Valencia y Extremadura
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8">
            <Card className="nubia-shadow border-0">
              <CardContent className="pt-6">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-nubia-gold-primary text-xl">★</span>
                  ))}
                </div>
                <p className="text-nubia-text-dark/80 mb-4 leading-relaxed">
                  "Instalación rápida y muy profesional. Desde el primer momento nos asesoraron en todo el proceso. Estamos muy contentos con el ahorro en nuestra factura."
                </p>
                <div className="text-sm font-semibold text-nubia-green-primary">
                  — María G., Madrid
                </div>
              </CardContent>
            </Card>

            <Card className="nubia-shadow border-0">
              <CardContent className="pt-6">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-nubia-gold-primary text-xl">★</span>
                  ))}
                </div>
                <p className="text-nubia-text-dark/80 mb-4 leading-relaxed">
                  "Gracias a las placas solares hemos reducido mucho la factura de la luz. El equipo de Nubia Solar fue muy profesional. Muy recomendables."
                </p>
                <div className="text-sm font-semibold text-nubia-green-primary">
                  — Carlos R., Getafe
                </div>
              </CardContent>
            </Card>

            <Card className="nubia-shadow border-0">
              <CardContent className="pt-6">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-nubia-gold-primary text-xl">★</span>
                  ))}
                </div>
                <p className="text-nubia-text-dark/80 mb-4 leading-relaxed">
                  "Equipo muy profesional y atención excelente durante toda la instalación. Nos tramitaron todas las ayudas sin problema. Totalmente satisfechos."
                </p>
                <div className="text-sm font-semibold text-nubia-green-primary">
                  — Ana L., Alcalá de Henares
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 nubia-gradient text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            ¿Listo para Empezar?
          </h2>
          <p className="text-xl md:text-2xl mb-12 max-w-3xl mx-auto text-nubia-white/90 leading-relaxed">
            Únete a más de 1,000 familias y empresas que ya disfrutan de energía limpia y facturas reducidas
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contacto">
              <Button size="lg" className="bg-nubia-gold-primary hover:bg-nubia-gold-primary/90 text-nubia-green-primary font-semibold px-10 py-7 text-lg shadow-xl">
                Solicitar Presupuesto
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-nubia-green-primary px-10 py-7 text-lg" onClick={() => window.location.href = 'tel:+34604921745'}>
              Llamar Ahora
            </Button>
          </div>
        </div>
      </section>

      {/* Cities Section */}
      <section className="py-20 bg-nubia-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-nubia-green-primary mb-4">
                Instalamos en Toda España
              </h2>
              <p className="text-xl text-nubia-text-dark/70">
                Presencia en las principales ciudades de Madrid, Valencia y Extremadura
              </p>
            </div>

            {/* Cities Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-10">
              {[
                { nombre: 'Madrid', slug: 'madrid' },
                { nombre: 'Valencia', slug: 'valencia' },
                { nombre: 'Alicante', slug: 'alicante' },
                { nombre: 'Badajoz', slug: 'badajoz' },
                { nombre: 'Cáceres', slug: 'caceres' },
                { nombre: 'Alcalá de Henares', slug: 'alcala-de-henares' },
                { nombre: 'Getafe', slug: 'getafe' },
                { nombre: 'Castellón', slug: 'castellon' },
                { nombre: 'Mérida', slug: 'merida' }
              ].map((ciudad) => (
                <Link 
                  key={ciudad.slug}
                  to={`/placas-solares/${ciudad.slug}`}
                  className="group"
                >
                  <div className="p-4 rounded-lg border-2 border-nubia-gray-light hover:border-nubia-gold-primary hover:bg-nubia-gold-soft/10 transition-all duration-300 text-center">
                    <span className="text-nubia-green-primary group-hover:text-nubia-gold-primary font-semibold transition-colors">
                      {ciudad.nombre}
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <div className="text-center">
              <Link to="/ciudades">
                <Button 
                  size="lg" 
                  className="bg-nubia-green-primary hover:bg-nubia-green-primary/90 text-white font-semibold px-10 py-6 shadow-lg"
                >
                  Ver Todas las Ciudades
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Inicio;
